import { User, UserListQueryParams } from '@nextforce/api'
import { useRoutingContext } from 'app/routing_context'
import { HomeStackParamList, UserListScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useShiftActions } from 'shifts/shift_actions_context'
import { useRecentStoresContext } from 'stores/context'
import { showStoreHeaderDropdown } from 'stores/dropdown'
import { useStores } from 'stores/queries/list'
import { crossStoreShiftAssignError } from 'stores/utils'
import { UserListView } from 'users/list/view'
import { useCrewSwapList } from 'users/queries/crew_swap_list'
import { useUser } from 'users/queries/detail'
import { useUserList } from 'users/queries/list'
import { UserListScreenParams } from './types'

export function UserListScreen({ navigation, route }: UserListScreenProps) {
  const screenType = route.params.type
  const isCrewInitiatedSwap = !!route.params?.isCrewInitiatedSwap
  const { setSelectedUser, actionCommand, onPerformSwapCover } =
    useShiftActions()
  const { handleNavigation } = useRoutingContext()
  const isAddAndAssign = !!route.params?.addAndAssign
  const { activeStoreId, recentStores, setActiveStoreId } =
    useRecentStoresContext()
  const { data: currentUser } = useUser()
  const { data: stores } = useStores(data => data.stores)
  const managerListQuery = useUserList(
    routeParamsToQueryParams(route.params),
    activeStoreId,
    !isCrewInitiatedSwap
  )
  const crewSwapListQuery = useCrewSwapList(
    routeParamsToQueryParams(route.params),
    activeStoreId,
    isCrewInitiatedSwap
  )

  const userListQuery = useMemo(() => {
    return isCrewInitiatedSwap ? crewSwapListQuery : managerListQuery
  }, [isCrewInitiatedSwap, crewSwapListQuery, managerListQuery])

  const crossStoreError = useMemo(() => {
    return crossStoreShiftAssignError(route.params?.store, activeStoreId)
  }, [route.params?.store, activeStoreId])

  const showStoreDropdown = currentUser?.permissions?.showStoreDropdown
    ? true
    : route.params?.store
    ? false
    : true

  useLayoutEffect(() => {
    if (showStoreDropdown) {
      showStoreHeaderDropdown(navigation, handleNavigation, {
        activeStoreId,
        recentStores,
        stores,
        setActiveStoreId,
      })
    }
  }, [
    activeStoreId,
    handleNavigation,
    navigation,
    recentStores,
    setActiveStoreId,
    showStoreDropdown,
    stores,
  ])

  useLayoutEffect(() => {
    if (userListQuery.data?.title) {
      showHeaderTitle(navigation, userListQuery.data.title)
    }
  }, [navigation, userListQuery.data?.title])

  const onAssignToCrew = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  const onPressAnytimeMetrics = useCallback(() => {
    handleNavigation({ routeName: 'anytime_crew_metrics' })
  }, [handleNavigation])

  const isSingleSelectList =
    !!userListQuery.data?.users &&
    userListQuery.data.users.length > 0 &&
    (isAddAndAssign || !userListQuery.data.users[0].route)

  const onPressUser = useCallback(
    (user: User) => {
      if (!user.route) {
        return
      }
      if (user.route.name === 'user_shifts') {
        handleNavigation({
          routeName: user.route.name,
          routeParams: {
            uri: user.route.params?.uri,
            command: actionCommand,
          },
        })
      } else {
        handleNavigation({
          routeName: user.route.name as keyof HomeStackParamList,
          routeParams: user.route.params && { ...user.route.params },
        })
      }
    },
    [actionCommand, handleNavigation]
  )

  return (
    <StatusBoundary
      isLoading={userListQuery.isLoading}
      error={userListQuery.error || crossStoreError}
    >
      <UserListView
        screenType={screenType}
        users={userListQuery.data?.users}
        onPressUser={onPressUser}
        isSingleSelectList={isSingleSelectList}
        onPerformSwapCover={onPerformSwapCover}
        command={actionCommand}
        noUsersNote={userListQuery.data?.noUsersNote}
        cancelHistory={route.params?.filter === 'recently_cancelled'}
        isAddAndAssign={isAddAndAssign}
        setSelectedCrew={setSelectedUser}
        onAssignToCrew={onAssignToCrew}
        onPressAnytimeMetrics={onPressAnytimeMetrics}
      />
    </StatusBoundary>
  )
}

function routeParamsToQueryParams(
  params?: UserListScreenParams
): UserListQueryParams {
  const queryParams: UserListQueryParams = {}
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      switch (key) {
        case 'type':
          queryParams['type'] = value
          break
        case 'filter':
          queryParams['filter'] = value
          break
        case 'filter_shift_id':
          queryParams['filter_shift_id'] = value
          break
        case 'size':
          queryParams['size'] = value
          break
        case 'shift_date':
          queryParams['shift_date'] = value
          break
        case 'start_time':
          queryParams['start_time'] = value
          break
        case 'end_time':
          queryParams['end_time'] = value
          break
        case 'position':
          queryParams['position'] = value
          break
      }
    }
  }
  return queryParams
}
